import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import {
  IHouseStepPlanedIntake,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable
} from "../../../interfaces";
import { getStepErrorsFromResponse } from "../../../utils";
import { WizardStepPlanIntakeView } from "../../../Host/Steps/WizardStepPlanIntake";
import { useContextHospi } from "../../../../../context/ContextHospi";
import { useTranslation } from "react-i18next";
import { useNotifier } from "../../../../../context/ContextNotifier";
import { useGetCalendlyHostIntakeMeetingUrl } from "../../../../Calendly/utils";
import { UserAPI } from "../../../../../API/userApi";

export const WizardStepPlanIntake: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const { userProfile, updateProfile } = useContextHospi();
    const [values, setValues] = useState<TStepPayload<IHouseStepPlanedIntake>>({
      from_dt: null,
      to_dt: null,
      done: Boolean(userProfile?.intake_data),
      event_id: null
    });
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    const { t } = useTranslation("house");
    const notifier = useNotifier();

    let calendlyUrl = useGetCalendlyHostIntakeMeetingUrl();

    const save = async () => {
      setErrors(null);

      if (userProfile?.intake_data || !values.event_id) {
        return state;
      }

      try {
        const updatedUser = await UserAPI.requestVerification(
          userProfile?.user_id!,
          values.event_id!
        );
        updateProfile(updatedUser);
        return state;
      } catch (e: any) {
        setErrors(getStepErrorsFromResponse(e));
        notifier.error({
          message: e?.toString() ?? t("wizard.errors.generic")
        });
        return Promise.reject(e);
      }
    };
    useImperativeHandle(ref, () => ({
      save: () => save(),
      savePartially: () => {
        return Promise.resolve(state);
      }
    }));

    const userHasIntakeData = Boolean(userProfile?.intake_data);
    useEffect(() => {
      setValues((prev) => ({
        ...prev,
        done: userHasIntakeData
      }));
    }, [userHasIntakeData]);

    return (
      <WizardStepPlanIntakeView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
        firstName={userProfile?.first_name!}
        lastName={userProfile?.last_name!}
        meetingUrl={calendlyUrl}
      />
    );
  });
